import { debounce } from '../utils/genericUtils.js'

const isNull = (param) => param === null

/**
 * @typedef {Object} StudentType
 * @property {string} ROUND
 * @property {string} AUTHORIZATION
 * @property {string} ROUTE
 */

export const StudentType = {
  ROUND: 'ROUND',
  AUTHORIZATION: 'AUTHORIZATION',
  ROUTE: 'AUTHORIZATION',
}

class Level {
  constructor({
    levelName = null,
    levelId = null,
    groupId = null,
    groupName = null,
  } = {}) {
    this.levelName = isNull(levelName) ? null : levelName
    this.levelId = isNull(levelId) ? null : levelId
    this.groupId = isNull(groupId) ? null : groupId
    this.groupName = isNull(groupName) ? null : groupName
  }
}

export class ChipLevel extends Level {
  constructor({ isSelected = false, ...props } = {}) {
    super(props)
    this.isSelected = isNull(isSelected) ? null : isSelected
  }
}

export const parseLevelsFromArray = (levels = []) =>
  levels.map((e) => new ChipLevel({ levelId: e.id, levelName: e.typeLevel }))

export const parseGroupsFromArray = (gradeGroups = []) =>
  gradeGroups
    .map((e) =>
      e.ggs.map(
        (x) =>
          new ChipLevel({
            groupId: x.id,
            levelId: e.id,
            levelName: e.typeLevel,
            groupName: `${x.grade} ${x.group}`,
          })
      )
    )
    .flat()

export const parseSubGroups = (items = []) =>
  items.map(
    (e) =>
      new ChipLevel({
        groupId: e.id,
        levelId: e.idLevel,
        levelName: e.level.typeLevel,
        groupName: e.name,
      })
  )

export class Student {
  constructor({ id = null, firstName = null, lastName = null } = {}) {
    this.id = isNull(id) ? null : id
    this.firstName = isNull(firstName) ? null : firstName
    this.lastName = isNull(lastName) ? null : lastName
    this.fullName = `${this.firstName} ${this.lastName}`
  }
}

export class ChipStudent extends Student {
  constructor({ isSelected = false, ...props } = {}) {
    super(props)
    this.isSelected = isNull(isSelected) ? null : isSelected
  }
}

export const parseChipStudentsFromArray = (students = []) =>
  students.map((e) => new ChipStudent(e))

export class StudentsOrSubgroupHandler {
  /**
   * @typedef {Object} StudentsParams
   * @property {number|undefined} idRoute
   * @property {StudentType} type
   */

  /**
   * @param {Object} param
   * @param {number} param.targetId
   * @param {any} param.api
   * @param {Function} param.itemParser
   * @param {string} param.apiUrl
   * @param {StudentsParams} param.studentsParams
   */
  constructor({ targetId, api, apiUrl, itemParser, studentsParams } = {}) {
    this.targetId = targetId
    this.api = api
    this.apiUrl = apiUrl
    this.itemParser = itemParser

    /**
     * @type {StudentsParams}
     */
    this.studentsParams = studentsParams
  }

  page = 1
  totalPages = 0
  selectedItemsIds = new Map()
  searchItems = ''
  /**
   * @type {Array<ChipLevel|ChipStudent>}
   */
  possibleItems = []
  /**
   * @type {Array<ChipLevel|ChipStudent>}
   */
  resultSearchedItems = []
  loading = false

  getItemsDebounced = debounce(this.getItems)

  getItems() {
    if (this.targetId === 'groupId') {
      this.getSubgroups()
    } else {
      this.getStudents()
    }
  }

  async getSubgroups() {
    const rowsPerPage = 25
    this.loading = true

    try {
      const filters = {
        search: this.searchItems.trim() === '' ? undefined : this.searchItems,
        page_size: rowsPerPage,
        offset: rowsPerPage * (this.page - 1),
      }
      const { data } = await this.api.dispatch(this.apiUrl, filters)
      const groups = this.itemParser(data.items)
      const totalPages = Math.ceil(data.total / rowsPerPage)

      this.totalPages = data.items.length === 0 ? 0 : totalPages
      this.possibleItems = groups
      this.resultSearchedItems = this.possibleItems
      this.setSelectedItems()
    } catch (error) {
      console.log(error)
      this.possibleItems = []
      this.resultSearchedItems = []
      this.totalPages = 0
    } finally {
      this.loading = false
    }
  }

  async getStudents() {
    const rowsPerPage = 25
    this.loading = true

    try {
      const filters = {
        search: this.searchItems.trim() === '' ? undefined : this.searchItems,
        size: rowsPerPage,
        offset: rowsPerPage * (this.page - 1),
        type: this.studentsParams ? this.studentsParams.type : undefined,
        id_route: this.studentsParams ? this.studentsParams.idRoute : undefined,
      }
      const { data } = await this.api.dispatch(this.apiUrl, filters)

      const students = this.itemParser(data.data)
      const totalPages = Math.ceil(data.total / rowsPerPage)

      this.totalPages = data.length === 0 ? 0 : totalPages
      this.possibleItems = students
      this.resultSearchedItems = this.possibleItems
      this.setSelectedItems()
    } catch (error) {
      console.log(error)
      this.possibleItems = []
      this.resultSearchedItems = []
      this.totalPages = 0
    } finally {
      this.loading = false
    }
  }

  setSelectedItems() {
    this.possibleItems.forEach((el) => {
      if (this.selectedItemsIds.has(el[this.targetId])) {
        el.isSelected = true
      }
    })
  }

  changeItem = (level = new ChipLevel()) => {
    const index = this.possibleItems.findIndex(
      (e) => e[this.targetId] === level[this.targetId]
    )
    this.possibleItems[index].isSelected = !this.possibleItems[index].isSelected

    if (this.selectedItemsIds.has(level[this.targetId])) {
      this.selectedItemsIds.delete(level[this.targetId])
      return
    }

    this.selectedItemsIds.set(level[this.targetId], level[this.targetId])
  }

  /**
   *
   * @returns {Array<Number>}
   */
  selectedItemsToIds = () => {
    return Array.from(this.selectedItemsIds.values())
  }
}

export class LevelsGroupsHandler {
  constructor(targetId, itemParser, rawLevels) {
    this.targetId = targetId
    this.itemParser = itemParser
    this.setupItems(rawLevels)
  }

  targetId = ''
  page = 1
  totalPages = 0
  selectedItemsIds = new Map()
  search = ''
  /**
   * @type {Array<ChipLevel>}
   */
  possibleItems = []
  /**
   * @type {Array<ChipLevel>}
   */
  resultSearchedItems = []

  setupItems(rawLevels) {
    const items = this.itemParser(rawLevels)
    this.possibleItems = items
    this.resultSearchedItems = this.possibleItems
    this.setSelectedItems()
  }

  searchItemsHandler() {
    if (this.search.trim() === '') {
      this.resultSearchedItems = this.possibleItems
      return
    }

    this.resultSearchedItems = this.possibleItems.filter((e) =>
      `${e.levelName} ${e.groupName}`
        .toLowerCase()
        .includes(this.search.toLowerCase())
    )
  }

  setSelectedItems() {
    this.possibleItems.forEach((el) => {
      if (this.selectedItemsIds.has(el[this.targetId])) {
        el.isSelected = true
      }
    })
  }

  changeItem = (level = new ChipLevel()) => {
    const index = this.possibleItems.findIndex(
      (e) => e[this.targetId] === level[this.targetId]
    )
    this.possibleItems[index].isSelected = !this.possibleItems[index].isSelected

    if (this.selectedItemsIds.has(level[this.targetId])) {
      this.selectedItemsIds.delete(level[this.targetId])
      return
    }

    this.selectedItemsIds.set(level[this.targetId], level[this.targetId])
  }

  selectedItemsToIds = () => {
    return Array.from(this.selectedItemsIds.values())
  }
}

export class Destination {
  constructor({
    id = Math.random(),
    name = '',
    latitude,
    longitude,
    index = 0,
  } = {}) {
    this.id = id
    this.name = name
    this.latitude = latitude
    this.longitude = longitude
    this.index = index
  }
}
export class InitialAudienceData {
  /**
   *
   * @param {'INSTITUTION'|'LEVELS'|'EXTRA_LEVELS'|'GROUPS'|'SUBGROUPS'|'STUDENTS'} target
   * @param {Array<number>} itemsIds
   */
  constructor(target = 'INSTITUTION', itemsIds = []) {
    this.target = target
    this.itemsIds = itemsIds
  }
}
